/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import BookingLoginAction from "store/bookingLogin/BookingLoginAction";
import update from "immutability-helper";
import {Driver} from "types/entities/Driver";
import {Reservation} from "types/entities/Reservation";
import {ReservationNote} from "store/booking/BookingAction";
import {ReservationDriver} from "types/entities/ReservationDriver";
import {PackagesCartItem} from "types/entities/PackagesCartItem";

export interface BookingLoginReducerSetReservations {
    payload: Reservation[];
    type: typeof BookingLoginAction.SET_BOOKING_LOGIN_RESERVATIONS;
}

export interface BookingLoginReducerClearReservations {
    payload: Reservation[];
    type: typeof BookingLoginAction.CLEAR_BOOKING_LOGIN;
}

export interface BookingLoginReducerSetReservation {
    payload: Reservation;
    type: typeof BookingLoginAction.SET_BOOKING_LOGIN_RESERVATION;
}

/**
 * update the PackagesCartItem into the Selected Reservation
 */
export interface BookingLoginReducerSetSelectedReservationPackagesCartItem {
    payload: PackagesCartItem;
    type: typeof BookingLoginAction.SET_BOOKING_LOGIN_SELECTED_RESERVATION_PACKAGES_CART_ITEM;
}

/**
 * update the ReservationDriver into the Selected Reservation
 */
export interface BookingLoginReducerSetSelectedReservationReservationDriver {
    payload: ReservationDriver;
    type: typeof BookingLoginAction.SET_BOOKING_LOGIN_SELECTED_RESERVATION_RESERVATION_DRIVER;
}

/**
 * Remove the ReservationDriver into the Selected Reservation
 */
export interface BookingLoginReducerRemoveReservationDriverFromSelectedReservation {
    payload: number;
    type: typeof BookingLoginAction.REMOVE_RESERVATION_DRIVER_FROM_SELECTED_RESERVATION;
}

/**
 * Remove the ReservationDriver into the Selected Reservation
 */
export interface BookingLoginReducerRemovePackagesCartItemFromSelectedReservation {
    payload: number;
    type: typeof BookingLoginAction.REMOVE_PACKAGES_CART_ITEM_FROM_SELECTED_RESERVATION;
}

export interface BookingLoginReducerRemoveReservation {
    payload: Reservation;
    type: typeof BookingLoginAction.REMOVE_RESERVATION_FROM_LIST;
}

export interface BookingLoginReducerSetUser {
    payload: Driver;
    type: typeof BookingLoginAction.SET_BOOKING_LOGIN_USER;
}

export interface BookingLoginReducerPushReservationNote {
    payload: ReservationNote;
    type: typeof BookingLoginAction.PUSH_RESERVATION_NOTE;
}

export interface BookingLoginReducerSetDriverFamilyAccounts {
    payload: Driver;
    type: typeof BookingLoginAction.SET_DRIVER_FAMILY_ACCOUNTS;
}

export interface BookingLoginReducerResetBookingLoginState {
    payload: null;
    type: typeof BookingLoginAction.RESET;
}

type BookingLoginReducerAction =
    | BookingLoginReducerSetReservations
    | BookingLoginReducerRemovePackagesCartItemFromSelectedReservation
    | BookingLoginReducerRemoveReservationDriverFromSelectedReservation
    | BookingLoginReducerSetSelectedReservationReservationDriver
    | BookingLoginReducerSetSelectedReservationPackagesCartItem
    | BookingLoginReducerClearReservations
    | BookingLoginReducerRemoveReservation
    | BookingLoginReducerSetReservation
    | BookingLoginReducerSetUser
    | BookingLoginReducerPushReservationNote
    | BookingLoginReducerSetDriverFamilyAccounts
    | BookingLoginReducerResetBookingLoginState;

export interface BookingLoginReducerState {
    reservations: Reservation[];
    selectedReservation: Reservation | null;
    user: Driver | null;
}

class BookingLoginReducer {
    initialState: BookingLoginReducerState = {
        reservations: [],
        selectedReservation: null,
        user: null,
    };

    reducer = (state: BookingLoginReducerState = this.initialState, action: BookingLoginReducerAction): BookingLoginReducerState => {
        function updateReservation(state: BookingLoginReducerState, reservationIndex: number, changeset: any) {
            return update(state, {
                reservations: {
                    [reservationIndex]: changeset,
                },
            } as any);
        }

        switch (action.type) {
            case BookingLoginAction.SET_BOOKING_LOGIN_RESERVATIONS:
                return <BookingLoginReducerState>update(state, {
                    reservations: {
                        $set: action.payload,
                    },
                });

            case BookingLoginAction.SET_BOOKING_LOGIN_USER:
                return {
                    ...state,
                    user: action.payload,
                };

            case BookingLoginAction.CLEAR_BOOKING_LOGIN:
                return <BookingLoginReducerState>update(state, {
                    reservations: {
                        $set: action.payload,
                    },
                });

            case BookingLoginAction.PUSH_RESERVATION_NOTE:
                const reservationForNoteIndex = state.reservations.findIndex(reservation => reservation.id === action.payload.reservation_id);

                if (reservationForNoteIndex === -1) {
                    return state;
                }

                const noteIndex = state.reservations[reservationForNoteIndex].reservationNotes.findIndex(note => note.id === action.payload.id);

                if (noteIndex === -1) {
                    return updateReservation(state, reservationForNoteIndex, {
                        reservationNotes: {$push: [action.payload]},
                    } as any);
                }

                return updateReservation(state, reservationForNoteIndex, {
                    reservationNotes: {[noteIndex]: {$set: action.payload}},
                } as any);

            case BookingLoginAction.SET_BOOKING_LOGIN_RESERVATION:
                const foundReservationIndex = state.reservations.findIndex(reservation => reservation.id === action.payload.id);

                if (foundReservationIndex === -1) {
                    return update(state, {
                        reservations: {
                            $push: [action.payload],
                        },
                        selectedReservation: {
                            $set: action.payload,
                        },
                    });
                }

                return <BookingLoginReducerState>update(state, {
                    reservations: {
                        [foundReservationIndex]: {
                            $set: action.payload,
                        },
                    },
                    selectedReservation: {
                        $set: action.payload,
                    },
                });

            case BookingLoginAction.SET_BOOKING_LOGIN_SELECTED_RESERVATION_PACKAGES_CART_ITEM:
                if (!state.selectedReservation || !state.selectedReservation.packagesCart || !state.selectedReservation.packagesCart.packagesCartItems) {
                    return state;
                }

                const foundPackagesCartItemIndex = state.selectedReservation.packagesCart.packagesCartItems.findIndex(
                    packagesCartItem => packagesCartItem.id === action.payload.id
                );

                if (foundPackagesCartItemIndex === -1) {
                    return update(state, {
                        selectedReservation: {
                            packagesCart: {
                                packagesCartItems: {$push: [action.payload]},
                            },
                        },
                    });
                }

                return <BookingLoginReducerState>update(state, {
                    selectedReservation: {
                        packagesCart: {
                            packagesCartItems: {
                                [foundPackagesCartItemIndex]: {
                                    $set: action.payload,
                                },
                            },
                        },
                    },
                });

            case BookingLoginAction.SET_BOOKING_LOGIN_SELECTED_RESERVATION_RESERVATION_DRIVER:
                if (!state.selectedReservation || !state.selectedReservation.reservationDrivers) {
                    return state;
                }

                const foundReservationDriverIndex = state.selectedReservation.reservationDrivers.findIndex(
                    reservationDriver => reservationDriver.id === action.payload.id
                );

                if (foundReservationDriverIndex === -1) {
                    return update(state, {
                        selectedReservation: {
                            reservationDrivers: {$push: [action.payload]},
                        },
                    });
                }

                return <BookingLoginReducerState>update(state, {
                    selectedReservation: {
                        reservationDrivers: {
                            [foundReservationDriverIndex]: {
                                $set: action.payload,
                            },
                        },
                    },
                });

            case BookingLoginAction.REMOVE_RESERVATION_DRIVER_FROM_SELECTED_RESERVATION:
                if (!state.selectedReservation || !state.selectedReservation.reservationDrivers) {
                    return state;
                }

                const foundRemovalReservationDriverIndex = state.selectedReservation.reservationDrivers.findIndex(
                    reservationDriver => reservationDriver.id === action.payload
                );

                if (foundRemovalReservationDriverIndex === -1) {
                    return state;
                }

                return <BookingLoginReducerState>update(state, {
                    selectedReservation: {
                        reservationDrivers: {
                            $splice: [[foundRemovalReservationDriverIndex, 1]],
                        },
                    },
                });

            case BookingLoginAction.REMOVE_PACKAGES_CART_ITEM_FROM_SELECTED_RESERVATION:
                if (!state.selectedReservation || !state.selectedReservation.packagesCart || !state.selectedReservation.packagesCart.packagesCartItems) {
                    return state;
                }

                const packagesCartItems = state.selectedReservation.packagesCart.packagesCartItems;
                const foundRemovalPackagesCartItemIndex = packagesCartItems.findIndex(pci => pci.id === action.payload);

                if (foundRemovalPackagesCartItemIndex === -1) {
                    return state;
                }

                return <BookingLoginReducerState>update(state, {
                    selectedReservation: {
                        packagesCart: {
                            packagesCartItems: {
                                [foundRemovalPackagesCartItemIndex]: {
                                    reservation_driver_id: {
                                        $set: null,
                                    },
                                },
                            },
                        },
                    },
                });

            case BookingLoginAction.REMOVE_RESERVATION_FROM_LIST:
                console.log(state.reservations, action.payload);
                const foundRemoveReservationIndex = state.reservations.findIndex(reservation => reservation.id === action.payload.id);

                if (foundRemoveReservationIndex === -1) {
                    return state;
                }
                console.log("got here");
                return <BookingLoginReducerState>update(state, {
                    reservations: {
                        $splice: [[foundRemoveReservationIndex, 1]],
                    },
                });

            case BookingLoginAction.SET_DRIVER_FAMILY_ACCOUNTS:
                if (!state.user || !state.user.familyAccounts) {
                    return state;
                }

                const familyAccountDriverIndex = state.user.familyAccounts.findIndex(driver => driver.id === action.payload.id);

                if (familyAccountDriverIndex === -1) {
                    return update(state, {
                        user: {
                            familyAccounts: {
                                $push: [action.payload]
                            },
                        },
                    });
                }

                return <BookingLoginReducerState>update(state, {
                    user: {
                        familyAccounts: {
                            [familyAccountDriverIndex]: {
                                $set: action.payload,
                            },
                        },
                    },
                });

            case BookingLoginAction.RESET:
                return this.initialState;

            default:
                return state;
        }
    };
}

export default BookingLoginReducer;
